/**
 * Enum representing the different location types that can be used in the search bar.
 * These values are used to specify the type of location being searched for.
 * Use these constants when setting or checking the location type in search operations.
 * This enum is for 1.5 mapping only.
 */
export default class SearchBarLocationType {
  static get County() {
    return this.getInstance('County');
  }

  /**
   * Gets the Neighborhood location type.
   * @returns {SearchBarLocationType} The Neighborhood location type.
   */
  static get Neighborhood() {
    return this.getInstance('Neighborhood');
  }

  /**
   * Gets the Zip location type.
   * @returns {SearchBarLocationType} The Zip location type.
   */
  static get Zip() {
    return this.getInstance('Zip');
  }

  /**
   * Gets the City location type.
   * @returns {SearchBarLocationType} The City location type.
   */
  static get City() {
    return this.getInstance('City');
  }

  /**
   * Gets the School District location type.
   * @returns {SearchBarLocationType} The School District location type.
   */
  static get SchoolDistrict() {
    return this.getInstance('SchoolDistrict');
  }

  /**
   * Gets the Market location type.
   * @returns {SearchBarLocationType} The Market location type.
   */
  static get Market() {
    return this.getInstance('Market');
  }

  constructor(name) {
    this.name = name;
  }

  /**
   * A static object to hold instances of SearchBarLocationType.
   * This ensures that each location type is a singleton.
   */
  static fromKey(key) {
    const locationMap = {
      county: SearchBarLocationType.County,
      neighborhood: SearchBarLocationType.Neighborhood,
      zip: SearchBarLocationType.Zip,
      city: SearchBarLocationType.City,
      schooldistrict: SearchBarLocationType.SchoolDistrict,
      market: SearchBarLocationType.Market
    };
    return locationMap[key];
  }

  /**
   * Gets an instance of SearchBarLocationType based on the provided key.
   * If an instance with the same key already exists, it returns that instance.
   * Otherwise, it creates a new instance and stores it for future use.
   * @param {string} key - The key representing the location type.
   * @returns {SearchBarLocationType} An instance of SearchBarLocationType.
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new SearchBarLocationType(key);
    }
    return this.instances[key];
  }

  /**
   * Converts the location type to a string representation.
   * @returns {string} The string representation of the location type.
   */
  toString() {
    return `${this.name}`;
  }
}

Object.defineProperty(SearchBarLocationType, 'instances', { value: {} });